/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Flex,
	Text,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../../../ews-template-labels';
import Loading from '../../../../../../components/Loading';
import useStore from '../../../../../../store';
import { getYear } from '../../../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const Q_REPORT = gql`
	query getEarlyWarningLifeRatioReport($year: Float!, $companyType: InsuranceInstitutionsType!, $companyId: String) {
		getEarlyWarningLifeRatioReport(year: $year, companyType: $companyType, companyId: $companyId)
	}
`;

const M_SAVE_LIFE_RATIO = gql`
	mutation saveCalculatedLifeRatioStatement(
		$year: Float!
		$companyType: InsuranceInstitutionsType!
		$companyId: String!
		$data: JSON
		$requiredSolvency: Float!
		$companyRiskRanking: Float!
	) {
		saveCalculatedLifeRatioStatement(
			year: $year
			companyType: $companyType
			companyId: $companyId
			data: $data
			requiredSolvency: $requiredSolvency
			companyRiskRanking: $companyRiskRanking
		)
	}
`;

const USER_INPUT_CODE = ['SRL', 'CARL'];

export default function EWSLifeSpreadSheetTable({ year = null, companyId = null, sfp = null, sploci = null, ord = null, lifeDataExport = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const companyType = urlParams.get('companyType') || null;

	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState(null);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const [q1Cells, setQ1Cells] = useState([]);
	const [q2Cells, setQ2Cells] = useState([]);
	const [q3Cells, setQ3Cells] = useState([]);
	const [q4Cells, setQ4Cells] = useState([]);
	const [q5Cells, setQ5Cells] = useState([]);

	const [isEditing, setIsEditing] = useState(false);
	const [initialQ2Cells, setInitialQ2Cells] = useState([]);
	const [initialQ3Cells, setInitialQ3Cells] = useState([]);

	const ratioCells = [
		{ cells: q1Cells, setCells: setQ1Cells, index: 0 },
		{ cells: q2Cells, setCells: setQ2Cells, index: 1 },
		{ cells: q3Cells, setCells: setQ3Cells, index: 2 },
		{ cells: q4Cells, setCells: setQ4Cells, index: 3 },
		{ cells: q5Cells, setCells: setQ5Cells, index: 4 },
	];

	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);
	const [saveLifeRatio, { loading: loadingSaveLifeRatio }] = useMutation(M_SAVE_LIFE_RATIO, {
		onCompleted: () => {
			toast({
				title: t('Ratio saved successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
		},
		onError: (err) => {
			const message = err?.graphQLErrors?.[0]?.message || err?.message || 'something went wrong';
			toast({
				title: t(message),
				status: 'error',
				isClosable: true,
				position: 'bottom-right',
			});
		},
	});

	useEffect(() => {
		load();
	}, [year, companyType, companyId]);

	function load() {
		const variables = {
			year: getYear(year),
			companyType,
			companyId,
		};

		loadReport({ variables });
	}

	useEffect(() => {
		if (data?.getEarlyWarningLifeRatioReport) {
			setDetail(data?.getEarlyWarningLifeRatioReport);
		}
	}, [data]);

	useEffect(() => {
		if (detail && sploci && sfp && ord) {
			quarterDetail(detail);
			console.log('sfp', sfp);
			console.log('sploci', sploci);
		}
	}, [detail, sploci, sfp, ord]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.forEach(({ message }) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	const isQuarterObjectArray = (q) => Array.isArray(q) && q.length > 0 && !Array.isArray(q[0]) && q[0] && typeof q[0] === 'object' && 'code' in q[0];

	const isSpreadsheetCells = (q) =>
		Array.isArray(q) && q.length > 0 && Array.isArray(q[0]) && q[0].length > 0 && q[0][0] && Object.prototype.hasOwnProperty.call(q[0][0], 'value');

	async function calculateButton() {
		if (!detail) return;

		const variables = {
			year: getYear(year),
			companyType,
			companyId,
			data: {
				q2: q2Cells,
				q3: q3Cells,
				q4: q4Cells,
				q5: q5Cells,
			},
			requiredSolvency: 0,
			companyRiskRanking: 0,
		};

		await saveLifeRatio({ variables });

		lifeDataExport({
			labels: detail.labels,
			q1Cells,
			q2Cells,
			q3Cells,
			q4Cells,
			q5Cells,
		});
	}

	function emptyCells(labels = [], i, sfp = { cells: [] }, sploci = { cells: [] }, ord = { cells: [] }) {
		const getCells = (labelCode) => {
			switch (labelCode) {
				case 'CIGPL':
					return Array.isArray(sploci?.cells?.[2]) ? sploci?.cells[2] : [];
				case 'ERL':
					return [Array.isArray(sploci?.cells?.[25]) ? sploci?.cells[25] : [], Array.isArray(sploci?.cells?.[2]) ? sploci?.cells[2] : []];
				case 'IYRL':
					return [
						Array.isArray(sploci?.cells?.[32]) ? sploci?.cells[32] : [],
						Array.isArray(sfp?.cells?.[2]) ? sfp?.cells[2] : [],
						Array.isArray(sfp?.cells?.[3]) ? sfp?.cells[3] : [],
						Array.isArray(sfp?.cells?.[8]) ? sfp?.cells[8] : [],
						Array.isArray(sfp?.cells?.[9]) ? sfp?.cells[9] : [],
						Array.isArray(sfp?.cells?.[10]) ? sfp?.cells[10] : [],
						Array.isArray(sfp?.cells?.[11]) ? sfp?.cells[11] : [],
						Array.isArray(sfp?.cells?.[12]) ? sfp?.cells[12] : [],
						Array.isArray(sfp?.cells?.[13]) ? sfp?.cells[13] : [],
					];
				case 'LRIL':
					return [Array.isArray(ord?.cells?.[0]) ? ord?.cells[0] : [], Array.isArray(ord?.cells?.[1]) ? ord?.cells[1] : []];
				case 'NIGBTSEL':
					return [Array.isArray(ord?.cells?.[3]) ? ord?.cells[3] : [], Array.isArray(sfp?.cells?.[36]) ? sfp?.cells[36] : []];
				case 'RATSHEL':
					return [Array.isArray(ord?.cells?.[2]) ? ord?.cells[2] : [], Array.isArray(sfp?.cells?.[36]) ? sfp?.cells[36] : []];
				case 'AIARTSEL':
					return [
						Array.isArray(sfp?.cells?.[11]) ? sfp?.cells[11] : [],
						Array.isArray(sfp?.cells?.[12]) ? sfp?.cells[12] : [],
						Array.isArray(sfp?.cells?.[13]) ? sfp?.cells[13] : [],
						Array.isArray(sfp?.cells?.[36]) ? sfp?.cells[36] : [],
					];
				case 'PRETIAL':
					return [
						Array.isArray(sfp?.cells?.[8]) ? sfp?.cells?.[8] : [],
						Array.isArray(sfp?.cells?.[9]) ? sfp?.cells[9] : [],
						Array.isArray(sfp?.cells?.[10]) ? sfp?.cells[10] : [],
						Array.isArray(sfp?.cells?.[11]) ? sfp?.cells[11] : [],
						Array.isArray(sfp?.cells?.[12]) ? sfp?.cells[12] : [],
						Array.isArray(sfp?.cells?.[13]) ? sfp?.cells[13] : [],
					];
				case 'GRRL':
					return [Array.isArray(sploci?.cells?.[2]) ? sploci?.cells?.[2] : [], Array.isArray(sfp?.cells?.[36]) ? sfp?.cells[36] : []];
				case 'DRL':
					return [Array.isArray(sfp?.cells?.[54]) ? sfp?.cells?.[54] : [], Array.isArray(sfp?.cells?.[24]) ? sfp?.cells[24] : []];
				case 'LCRL':
					return [
						Array.isArray(sfp?.cells?.[39]) ? sfp?.cells?.[39] : [],
						Array.isArray(sfp?.cells?.[40]) ? sfp?.cells?.[40] : [],
						Array.isArray(sfp?.cells?.[41]) ? sfp?.cells?.[41] : [],
						Array.isArray(sfp?.cells?.[42]) ? sfp?.cells?.[42] : [],
						Array.isArray(sfp?.cells?.[36]) ? sfp?.cells?.[36] : [],
					];
				case 'NIRL':
					return [Array.isArray(sploci?.cells?.[47]) ? sploci?.cells?.[47] : [], Array.isArray(sploci?.cells?.[2]) ? sploci?.cells?.[2] : []];
				case 'ROEL':
					return [Array.isArray(sploci?.cells?.[47]) ? sploci?.cells?.[47] : [], Array.isArray(sfp?.cells?.[36]) ? sfp?.cells?.[36] : []];
				case 'ROAL':
					return [Array.isArray(sploci?.cells?.[47]) ? sploci?.cells?.[47] : [], Array.isArray(sfp?.cells?.[24]) ? sfp?.cells?.[24] : []];
				default:
					return [];
			}
		};

		const calculateScore = (labelCode, cells, i) => {
			switch (labelCode) {
				case 'CIGPL':
					const cig1 = cells;
					const cig1Cell = Number(cig1[0]?.value || 0) / Number(cig1[1]?.value || 0) - 1;
					const cig2Cell = Number(cig1[1]?.value || 0) / Number(cig1[2]?.value || 0) - 1;
					const riskCig1 =
						cig1Cell <= -60
							? 4
							: cig1Cell <= -50
							? 3
							: cig1Cell <= -40
							? 2
							: cig1Cell <= -30
							? 1
							: cig1Cell <= 0
							? 0
							: cig1Cell <= 30
							? 0
							: cig1Cell <= 40
							? 1
							: cig1Cell <= 50
							? 2
							: cig1Cell <= 60
							? 3
							: cig1Cell <= 70
							? 4
							: 0;
					const riskCig2 =
						cig2Cell <= -60
							? 4
							: cig2Cell <= -50
							? 3
							: cig2Cell <= -40
							? 2
							: cig2Cell <= -30
							? 1
							: cig2Cell <= 0
							? 0
							: cig2Cell <= 30
							? 0
							: cig2Cell <= 40
							? 1
							: cig2Cell <= 50
							? 2
							: cig2Cell <= 60
							? 3
							: cig2Cell <= 70
							? 4
							: 0;

					switch (i) {
						case 0:
							return cig1Cell;
						case 1:
							return cig2Cell;
						case 2:
							return riskCig1;
						case 3:
							return riskCig2;
						default:
							return 0;
					}

				case 'ERL':
					const [a1, a2] = cells;
					const a1Cell = -(Number(a1[0]?.value || 0) / Number(a2[0]?.value || 0));
					const a2Cell = -(Number(a1[1]?.value || 0) / Number(a2[1]?.value || 0));
					const a1Pct = a1Cell * 100;
					const a2Pct = a2Cell * 100;
					const riskA1 =
						a1Pct <= -20
							? 4
							: a1Pct <= -10
							? 3
							: a1Pct <= 0
							? 2
							: a1Pct < 10
							? 1
							: a1Pct === 10
							? 0
							: a1Pct <= 50
							? 0
							: a1Pct <= 60
							? 1
							: a1Pct <= 70
							? 2
							: a1Pct <= 80
							? 3
							: a1Pct <= 90
							? 4
							: 0;
					const riskA2 =
						a2Pct <= -20
							? 4
							: a2Pct <= -10
							? 3
							: a2Pct <= 0
							? 2
							: a2Pct < 10
							? 1
							: a2Pct === 10
							? 0
							: a2Pct <= 50
							? 0
							: a2Pct <= 60
							? 1
							: a2Pct <= 70
							? 2
							: a2Pct <= 80
							? 3
							: a2Pct <= 90
							? 4
							: 0;
					switch (i) {
						case 0:
							return a1Cell;
						case 1:
							return a2Cell;
						case 2:
							return riskA1;
						case 3:
							return riskA2;
						default:
							return 0;
					}
				case 'IYRL':
					const [c1, c2, c3, c4, c5, c6, c7, c8, c9] = cells;
					const c1Cell =
						Number(c1[0]?.value || 0) /
						((Number(c2[0]?.value || 0) +
							Number(c3[0]?.value || 0) +
							Number(c4[0]?.value || 0) +
							Number(c5[0]?.value || 0) +
							Number(c6[0]?.value || 0) +
							Number(c7[0]?.value || 0) +
							Number(c8[0]?.value || 0) +
							Number(c9[0]?.value || 0) +
							Number(c2[1]?.value || 0) +
							Number(c3[1]?.value || 0) +
							Number(c4[1]?.value || 0) +
							Number(c5[1]?.value || 0) +
							Number(c6[1]?.value || 0) +
							Number(c7[1]?.value || 0) +
							Number(c8[1]?.value || 0) +
							Number(c9[1]?.value || 0)) /
							2);
					const c2Cell =
						Number(c1[1]?.value || 0) /
						((Number(c2[1]?.value || 0) +
							Number(c3[1]?.value || 0) +
							Number(c4[1]?.value || 0) +
							Number(c5[1]?.value || 0) +
							Number(c6[1]?.value || 0) +
							Number(c7[1]?.value || 0) +
							Number(c8[1]?.value || 0) +
							Number(c9[1]?.value || 0) +
							Number(c2[2]?.value || 0) +
							Number(c3[2]?.value || 0) +
							Number(c4[2]?.value || 0) +
							Number(c5[2]?.value || 0) +
							Number(c6[2]?.value || 0) +
							Number(c7[2]?.value || 0) +
							Number(c8[2]?.value || 0) +
							Number(c9[2]?.value || 0)) /
							2);
					const c1Pct = c1Cell * 100;
					const c2Pct = c2Cell * 100;
					const riskC1 =
						c1Pct <= -27
							? 4
							: c1Pct <= -17
							? 3
							: c1Pct <= -7
							? 2
							: c1Pct < 3
							? 1
							: c1Pct === 3
							? 0
							: c1Pct <= 10
							? 0
							: c1Pct <= 20
							? 1
							: c1Pct <= 30
							? 2
							: c1Pct <= 40
							? 3
							: c1Pct <= 50
							? 4
							: 0;
					const riskC2 =
						c2Pct <= -27
							? 4
							: c2Pct <= -17
							? 3
							: c2Pct <= -7
							? 2
							: c2Pct < 3
							? 1
							: c2Pct === 3
							? 0
							: c2Pct <= 10
							? 0
							: c2Pct <= 20
							? 1
							: c2Pct <= 30
							? 2
							: c2Pct <= 40
							? 3
							: c2Pct <= 50
							? 4
							: 0;
					switch (i) {
						case 0:
							return c1Cell;
						case 1:
							return c2Cell;
						case 2:
							return riskC1;
						case 3:
							return riskC2;
						default:
							return 0;
					}
				case 'LRIL':
					const [g1, g2] = cells;
					const g1Cell = Number(g1[0]?.value || 0) / Number(g2[0]?.value || 0);
					const g2Cell = Number(g1[1]?.value || 0) / Number(g2[1]?.value || 0);
					const g1Pct = g1Cell * 100;
					const g2Pct = g2Cell * 100;
					const riskG1 = g1Pct >= 110 ? 0 : g1Pct >= 100 ? 1 : g1Pct >= 90 ? 2 : g1Pct >= 90 ? 3 : 4;
					const riskG2 = g2Pct >= 110 ? 0 : g2Pct >= 100 ? 1 : g2Pct >= 90 ? 2 : g2Pct >= 90 ? 3 : 4;
					switch (i) {
						case 0:
							return g1Cell;
						case 1:
							return g2Cell;
						case 2:
							return riskG1;
						case 3:
							return riskG2;
						default:
							return 0;
					}
				case 'NIGBTSEL':
					const [d1, d2] = cells;
					const d1Cell = Number(d1[0]?.value || 0) / Number(d2[0]?.value || 0);
					const d2Cell = Number(d1[1]?.value || 0) / Number(d2[1]?.value || 0);
					const d1Pct = d1Cell * 100;
					const d2Pct = d2Cell * 100;
					const riskD1 = d1Pct <= 10 ? 0 : d1Pct <= 20 ? 1 : d1Pct <= 30 ? 2 : d1Pct < 40 ? 3 : 4;
					const riskD2 = d2Pct <= 10 ? 0 : d2Pct <= 20 ? 1 : d2Pct <= 30 ? 2 : d2Pct < 40 ? 3 : 4;
					switch (i) {
						case 0:
							return d1Cell;
						case 1:
							return d2Cell;
						case 2:
							return riskD1;
						case 3:
							return riskD2;
						default:
							return 0;
					}
				case 'RATSHEL':
					const [l1, l2] = cells;
					const l1Cell = Number(l1[0]?.value || 0) / Number(l2[0]?.value || 0);
					const l2Cell = Number(l1[1]?.value || 0) / Number(l2[1]?.value || 0);
					const l1Pct = l1Cell * 100;
					const l2Pct = l2Cell * 100;
					const riskl1 = l1Pct <= 10 ? 0 : l1Pct <= 20 ? 1 : l1Pct <= 30 ? 2 : l1Pct < 40 ? 3 : 4;
					const riskl2 = l2Pct <= 10 ? 0 : l2Pct <= 20 ? 1 : l2Pct <= 30 ? 2 : l2Pct < 40 ? 3 : 4;
					switch (i) {
						case 0:
							return l1Cell;
						case 1:
							return l2Cell;
						case 2:
							return riskl1;
						case 3:
							return riskl2;
						default:
							return 0;
					}
				case 'AIARTSEL':
					const [ai1, ai2, ai3, ai4] = cells;
					const ai1Cell = (Number(ai1[0]?.value || 0) + Number(ai2[0]?.value || 0) + Number(ai3[0]?.value || 0)) / Number(ai4[0]?.value || 0);
					const ai2Cell = (Number(ai1[1]?.value || 0) + Number(ai2[1]?.value || 0) + Number(ai3[1]?.value || 0)) / Number(ai4[1]?.value || 0);
					const ai1Pct = ai1Cell * 100;
					const ai2Pct = ai2Cell * 100;
					const riskAi1 = ai1Pct <= 100 ? 0 : ai1Pct <= 90 ? 1 : ai1Pct <= 80 ? 2 : ai1Pct <= 70 ? 3 : 4;
					const riskAi2 = ai2Pct <= 100 ? 0 : ai2Pct <= 90 ? 1 : ai2Pct <= 80 ? 2 : ai2Pct <= 70 ? 3 : 4;
					switch (i) {
						case 0:
							return ai1Cell;
						case 1:
							return ai2Cell;
						case 2:
							return riskAi1;
						case 3:
							return riskAi2;
						default:
							return 0;
					}
				case 'PRETIAL':
					const [p1, p2, p3, p4, p5, p6] = cells;
					const p1Cell =
						Number(p1[0]?.value || 0) /
						(Number(p1[0]?.value || 0) +
							Number(p2[0]?.value || 0) +
							Number(p3[0]?.value || 0) +
							Number(p4[0]?.value || 0) +
							Number(p5[0]?.value || 0) +
							Number(p6[0]?.value || 0));
					const p2Cell =
						Number(p1[1]?.value || 0) /
						(Number(p1[1]?.value || 0) +
							Number(p2[1]?.value || 0) +
							Number(p3[1]?.value || 0) +
							Number(p4[1]?.value || 0) +
							Number(p5[1]?.value || 0) +
							Number(p6[1]?.value || 0));
					const p1Pct = p1Cell * 100;
					const p2Pct = p2Cell * 100;
					const riskP1 = p1Pct <= 20 ? 0 : p1Pct <= 10 ? 1 : p1Pct <= 0 ? 2 : p1Pct < 10 ? 3 : 4;
					const riskP2 = p2Pct <= 20 ? 0 : p2Pct <= 10 ? 1 : p2Pct <= 0 ? 2 : p2Pct < 10 ? 3 : 4;
					switch (i) {
						case 0:
							return p1Cell;
						case 1:
							return p2Cell;
						case 2:
							return riskP1;
						case 3:
							return riskP2;
						default:
							return 0;
					}
				case 'GRRL':
					const [r1, r2] = cells;
					const r1Cell = Number(r1[0]?.value || 0) / Number(r2[0]?.value || 0);
					const r2Cell = Number(r1[1]?.value || 0) / Number(r2[1]?.value || 0);
					const r1Pct = r1Cell * 100;
					const r2Pct = r2Cell * 100;
					const riskR1 = r1Pct <= 800 ? 0 : r1Pct <= 900 ? 1 : r1Pct <= 1000 ? 2 : r1Pct <= 1100 ? 3 : 4;
					const riskR2 = r2Pct <= 800 ? 0 : r2Pct <= 900 ? 1 : r2Pct <= 1000 ? 2 : r2Pct <= 1100 ? 3 : 4;
					switch (i) {
						case 0:
							return r1Cell;
						case 1:
							return r2Cell;
						case 2:
							return riskR1;
						case 3:
							return riskR2;
						default:
							return 0;
					}
				case 'DRL':
					const [dr1, dr2] = cells;
					const dr1Cell = Number(dr1[0]?.value || 0) / Number(dr2[0]?.value || 0);
					const dr2Cell = Number(dr1[1]?.value || 0) / Number(dr2[1]?.value || 0);
					const dr1Pct = dr1Cell * 100;
					const dr2Pct = dr2Cell * 100;
					const riskDr1 =
						dr1Pct <= 0
							? 4
							: dr1Pct <= 10
							? 3
							: dr1Pct <= 20
							? 2
							: dr1Pct < 30
							? 1
							: dr1Pct === 40
							? 0
							: dr1Pct <= 60
							? 0
							: dr1Pct <= 70
							? 1
							: dr1Pct <= 80
							? 2
							: dr1Pct <= 90
							? 3
							: dr1Pct <= 100
							? 4
							: 0;
					const riskDr2 =
						dr2Pct <= 0
							? 4
							: dr2Pct <= 10
							? 3
							: dr2Pct <= 20
							? 2
							: dr2Pct < 30
							? 1
							: dr2Pct === 40
							? 0
							: dr2Pct <= 60
							? 0
							: dr2Pct <= 70
							? 1
							: dr2Pct <= 80
							? 2
							: dr2Pct <= 90
							? 3
							: dr2Pct <= 100
							? 4
							: 0;
					switch (i) {
						case 0:
							return dr1Cell;
						case 1:
							return dr2Cell;
						case 2:
							return riskDr1;
						case 3:
							return riskDr2;
						default:
							return 0;
					}
				case 'LCRL':
					const [lcr1, lcr2, lcr3, lcr4, lcr5] = cells;
					const lcr1Cell =
						(Number(lcr1[0]?.value || 0) + Number(lcr2[0]?.value || 0) + Number(lcr3[0]?.value || 0) + Number(lcr4[0]?.value || 0)) /
						Number(lcr5[0]?.value);
					const lcr2Cell =
						(Number(lcr1[1]?.value || 0) + Number(lcr2[1]?.value || 0) + Number(lcr3[1]?.value || 0) + Number(lcr4[1]?.value || 0)) /
						Number(lcr5[1]?.value);
					const lcr1Pct = lcr1Cell * 100;
					const lcr2Pct = lcr2Cell * 100;
					const riskL1 = lcr1Pct <= 700 ? 0 : lcr1Pct <= 800 ? 1 : lcr1Pct <= 900 ? 2 : lcr1Pct <= 1000 ? 3 : 4;
					const riskL2 = lcr2Pct <= 700 ? 0 : lcr2Pct <= 800 ? 1 : lcr2Pct <= 900 ? 2 : lcr2Pct <= 1000 ? 3 : 4;
					switch (i) {
						case 0:
							return lcr1Cell;
						case 1:
							return lcr2Cell;
						case 2:
							return riskL1;
						case 3:
							return riskL2;
						default:
							return 0;
					}
				case 'NIRL':
					const [nr1, nr2] = cells;
					const nr1Cell = Number(nr1[0]?.value || 0) / Number(nr2[0]?.value || 0);
					const nr2Cell = Number(nr1[1]?.value || 0) / Number(nr2[1]?.value || 0);
					const nr1Pct = nr1Cell * 100;
					const nr2Pct = nr2Cell * 100;
					const riskNr1 =
						nr1Pct <= -30
							? 4
							: nr1Pct <= -20
							? 3
							: nr1Pct <= -10
							? 2
							: nr1Pct < 0
							? 1
							: nr1Pct === 30
							? 0
							: nr1Pct <= 10
							? 0
							: nr1Pct <= 20
							? 1
							: nr1Pct <= 30
							? 2
							: nr1Pct <= 40
							? 3
							: nr1Pct <= 50 || nr1Pct > 50
							? 4
							: 0;
					const riskNr2 =
						nr2Pct <= -30
							? 4
							: nr2Pct <= -20
							? 3
							: nr2Pct <= -10
							? 2
							: nr2Pct < 0
							? 1
							: nr2Pct === 30
							? 0
							: nr2Pct <= 10
							? 0
							: nr2Pct <= 20
							? 1
							: nr2Pct <= 30
							? 2
							: nr2Pct <= 40
							? 3
							: nr2Pct <= 50 || nr2Pct > 50
							? 4
							: 0;
					switch (i) {
						case 0:
							return nr1Cell;
						case 1:
							return nr2Cell;
						case 2:
							return riskNr1;
						case 3:
							return riskNr2;
						default:
							return 0;
					}
				case 'ROEL':
					const [roe1, roe2] = cells;
					const roe1Cell = Number(roe1[0]?.value || 0) / Number(roe2[0]?.value || 0);
					const roe2Cell = Number(roe1[1]?.value || 0) / Number(roe2[1]?.value || 0);
					const roe1Pct = roe1Cell * 100;
					const roe2Pct = roe2Cell * 100;
					const riskRoe1 =
						roe1Pct <= -20
							? 4
							: roe1Pct <= -10
							? 3
							: roe1Pct <= 0
							? 2
							: roe1Pct < 10
							? 1
							: roe1Pct === 10
							? 0
							: roe1Pct <= 15
							? 0
							: roe1Pct <= 25
							? 1
							: roe1Pct <= 35
							? 2
							: roe1Pct <= 45
							? 3
							: roe1Pct <= 55 || roe1Pct > 55
							? 4
							: 0;
					const riskRoe2 =
						roe2Pct <= -20
							? 4
							: roe2Pct <= -10
							? 3
							: roe2Pct <= 0
							? 2
							: roe2Pct < 10
							? 1
							: roe2Pct === 10
							? 0
							: roe2Pct <= 15
							? 0
							: roe2Pct <= 25
							? 1
							: roe2Pct <= 35
							? 2
							: roe2Pct <= 45
							? 3
							: roe2Pct <= 55 || roe2Pct > 55
							? 4
							: 0;
					switch (i) {
						case 0:
							return roe1Cell;
						case 1:
							return roe2Cell;
						case 2:
							return riskRoe1;
						case 3:
							return riskRoe2;
						default:
							return 0;
					}
				case 'ROAL':
					const [roa1, roa2] = cells;
					const roa1Cell = Number(roa1[0]?.value || 0) / Number(roa2[0]?.value || 0);
					const roa2Cell = Number(roa1[1]?.value || 0) / Number(roa2[1]?.value || 0);
					const roa1Pct = roa1Cell * 100;
					const roa2Pct = roa2Cell * 100;
					const riskRoa1 =
						roa1Pct <= -25
							? 4
							: roa1Pct <= -15
							? 3
							: roa1Pct <= -5
							? 2
							: roa1Pct < 5
							? 1
							: roa1Pct === 5
							? 0
							: roa1Pct <= 20
							? 0
							: roa1Pct <= 30
							? 1
							: roa1Pct <= 40
							? 2
							: roa1Pct <= 50
							? 3
							: roa1Pct <= 60 || roa1Pct > 60
							? 4
							: 0;
					const riskRoa2 =
						roa2Pct <= -25
							? 4
							: roa2Pct <= -15
							? 3
							: roa2Pct <= -5
							? 2
							: roa2Pct < 5
							? 1
							: roa2Pct === 5
							? 0
							: roa2Pct <= 20
							? 0
							: roa2Pct <= 30
							? 1
							: roa2Pct <= 40
							? 2
							: roa2Pct <= 50
							? 3
							: roa2Pct <= 60 || roa2Pct > 60
							? 4
							: 0;
					switch (i) {
						case 0:
							return roa1Cell;
						case 1:
							return roa2Cell;
						case 2:
							return riskRoa1;
						case 3:
							return riskRoa2;
						default:
							return 0;
					}
				default:
					return 0;
			}
		};

		return labels?.map((label) => {
			if (label?.readOnly) return [{ value: '', readOnly: true }];

			const cells = getCells(label?.code);
			const score = calculateScore(label?.code, cells, i);

			if (i === 0 || i === 1) {
				const percent = Number.isFinite(score) ? (score * 100).toFixed(2) + '%' : '';

				return [
					{
						value: percent,
						readOnly: true,
						style: {
							textAlign: 'center',
							fontWeight: 'bold',
						},
					},
				];
			}

			const riskValue = score.toFixed(2);

			return [
				{
					value: riskValue,
					readOnly: true,
					style: {
						textAlign: 'center',
						fontWeight: 'bold',
					},
				},
			];
		});
	}

	function quarterCells({ labels = [], quarter = [] }) {
		const localCells = labels.map((label) => {
			const rowForLabel = quarter.find((q) => q?.code === label?.code);
			const fallbackValues = [{ value: '', readOnly: true }];
			return rowForLabel?.values || fallbackValues;
		});

		return localCells;
	}

	function normCells({ labels = [] }) {
		const cells = labels?.map((label) => {
			const normValue = label?.normsValue || '';
			return [{ value: normValue, readOnly: true, style: { textAlign: 'center' } }];
		});

		return cells;
	}

	const formatUserInputCellsAsPercent = (labels, cells) => {
		if (!Array.isArray(cells)) return cells;

		return cells?.map((row, rowIndex) => {
			const code = labels?.[rowIndex]?.code;
			if (!USER_INPUT_CODE?.includes(code)) {
				return row;
			}

			return row?.map((cell) => {
				if (!cell) return cell;
				const raw = cell?.value;

				if (raw === '' || raw == null) return cell;

				const str = String(raw).trim();

				if (str.endsWith('%')) return cell;

				const num = parseFloat(str);
				if (!Number.isFinite(num)) return cell;

				return {
					...cell,
					value: `${num.toFixed(2)}%`,
				};
			});
		});
	};

	function quarterDetail(detailData) {
		if (!detailData || !detailData?.labels) return;

		const labels = detailData?.labels;
		const normsCell = normCells({ labels });

		// q2: Current Ratio
		let currentRatioCellsRaw;
		if (isSpreadsheetCells(detailData?.q2)) {
			currentRatioCellsRaw = detailData?.q2;
		} else if (isQuarterObjectArray(detailData?.q2)) {
			currentRatioCellsRaw = quarterCells({ labels, quarter: detailData?.q2 });
		} else {
			currentRatioCellsRaw = emptyCells(labels, 0, sfp, sploci, ord);
		}

		// q3: Previous Ratio
		let previousRatioCellsRaw;
		if (isSpreadsheetCells(detailData?.q3)) {
			previousRatioCellsRaw = detailData?.q3;
		} else if (isQuarterObjectArray(detailData?.q3)) {
			previousRatioCellsRaw = quarterCells({ labels, quarter: detailData?.q3 });
		} else {
			previousRatioCellsRaw = emptyCells(labels, 1, sfp, sploci, ord);
		}

		// q4: Current Risk
		let currentRiskFlagCellsRaw;
		if (isSpreadsheetCells(detailData?.q4)) {
			currentRiskFlagCellsRaw = detailData?.q4;
		} else {
			currentRiskFlagCellsRaw = emptyCells(labels, 2, sfp, sploci, ord);
		}

		// q5: Previous Risk
		let previousRiskFlagCellsRaw;
		if (isSpreadsheetCells(detailData?.q5)) {
			previousRiskFlagCellsRaw = detailData?.q5;
		} else {
			previousRiskFlagCellsRaw = emptyCells(labels, 3, sfp, sploci, ord);
		}

		const numRows = detailData?.labels?.length;
		const normalize = (cells) => (cells?.length === numRows ? cells : [...cells, ...Array(numRows - cells?.length).fill([{ value: '', readOnly: true }])]);

		const makeManualEditable = (cells) =>
			cells?.map((row, rowIndex) => {
				const code = labels?.[rowIndex]?.code;
				if (USER_INPUT_CODE?.includes(code)) {
					return row?.map((cell) => ({
						...cell,
						readOnly: false,
					}));
				}
				return row;
			});

		const normsCellNorm = normalize(normsCell);
		const currentRatioFormatted = formatUserInputCellsAsPercent(labels, currentRatioCellsRaw);
		const previousRatioFormatted = formatUserInputCellsAsPercent(labels, previousRatioCellsRaw);

		const currentRatioCells = normalize(makeManualEditable(currentRatioFormatted));
		const previousRatioCells = normalize(makeManualEditable(previousRatioFormatted));
		const currentRiskFlagCells = normalize(currentRiskFlagCellsRaw);
		const previousRiskFlagCells = normalize(previousRiskFlagCellsRaw);

		setQ1Cells(normsCellNorm);
		setQ2Cells(currentRatioCells);
		setQ3Cells(previousRatioCells);
		setQ4Cells(currentRiskFlagCells);
		setQ5Cells(previousRiskFlagCells);

		setInitialQ2Cells(currentRatioCells);
		setInitialQ3Cells(previousRatioCells);
		setIsEditing(false);

		lifeDataExport({
			labels,
			q1Cells: normsCell,
			q2Cells: currentRatioCells,
			q3Cells: previousRatioCells,
			q4Cells: currentRiskFlagCells,
			q5Cells: previousRiskFlagCells,
		});
	}

	const parseRatioValue = (cellValue) => {
		if (cellValue == null) return 0;
		if (typeof cellValue === 'number') return cellValue;

		const cleaned = String(cellValue).replace('%', '').trim();
		const num = parseFloat(cleaned);
		return Number.isFinite(num) ? num : 0;
	};

	const getRiskLevelForUserInput = (code, ratio) => {
		switch (code) {
			case 'SRL':
				if (ratio >= 120) return 0;
				if (ratio >= 110) return 1;
				if (ratio >= 100) return 2;
				if (ratio >= 90) return 3;
				return 4;

			case 'CARL':
				if (ratio >= 100) return 0;
				if (ratio >= 90) return 1;
				if (ratio >= 80) return 2;
				if (ratio >= 70) return 3;
				return 4;

			default:
				return 0;
		}
	};

	// Current Risk Flag (q4)
	useEffect(() => {
		if (!detail?.labels || !q2Cells?.length) return;

		const base = q4Cells?.length
			? q4Cells
			: detail?.labels?.map(() => [
					{
						value: '',
						readOnly: true,
						style: { textAlign: 'center', fontWeight: 'bold' },
					},
			  ]);

		const updated = detail?.labels?.map((label, rowIndex) => {
			const baseCell = base[rowIndex]?.[0] || {
				value: '',
				readOnly: true,
				style: { textAlign: 'center', fontWeight: 'bold' },
			};

			if (!USER_INPUT_CODE?.includes(label?.code)) {
				return [baseCell];
			}

			const ratioCell = q2Cells?.[rowIndex]?.[0] || { value: '' };
			const ratioNumber = parseRatioValue(ratioCell?.value);
			const riskLevel = getRiskLevelForUserInput(label?.code, ratioNumber);

			return [
				{
					...baseCell,
					value: riskLevel.toFixed(2),
					readOnly: true,
				},
			];
		});

		setQ4Cells(updated);
	}, [q2Cells, detail]);

	// Previous Risk Flag (q5)
	useEffect(() => {
		if (!detail?.labels || !q3Cells?.length) return;

		const base = q5Cells?.length
			? q5Cells
			: detail?.labels?.map(() => [
					{
						value: '',
						readOnly: true,
						style: { textAlign: 'center', fontWeight: 'bold' },
					},
			  ]);

		const updated = detail?.labels?.map((label, rowIndex) => {
			const baseCell = base[rowIndex]?.[0] || {
				value: '',
				readOnly: true,
				style: { textAlign: 'center', fontWeight: 'bold' },
			};

			if (!USER_INPUT_CODE.includes(label?.code)) {
				return [baseCell];
			}

			const ratioCell = q3Cells?.[rowIndex]?.[0] || { value: '' };
			const ratioNumber = parseRatioValue(ratioCell.value);
			const riskLevel = getRiskLevelForUserInput(label.code, ratioNumber);

			return [
				{
					...baseCell,
					value: riskLevel.toFixed(2),
					readOnly: true,
				},
			];
		});

		setQ5Cells(updated);
	}, [q3Cells, detail]);

	function handleCellsChange(index, newData) {
		switch (index) {
			case 0:
				setQ1Cells(newData);
				break;
			case 1:
				setQ2Cells(newData);
				break;
			case 2:
				setQ3Cells(newData);
				break;
			case 3:
				setQ4Cells(newData);
				break;
			case 4:
				setQ5Cells(newData);
				break;
			default:
				break;
		}
	}

	const isDifferent = (a = [], b = []) => {
		if (!a || !b) return true;
		if (a?.length !== b?.length) return true;
		return a?.some((row, rIndex) => row?.some((cell, cIndex) => cell?.value !== b[rIndex]?.[cIndex]?.value));
	};

	return (
		<Box>
			{loading && <Loading />}

			{!loading && !detail && (
				<Text color="gray.400" textAlign="center" mt={4}>
					{t('No Data')}
				</Text>
			)}

			{!loading && detail && (
				<Box className="ews-container">
					<Flex mb={2} justifyContent="flex-end" alignItems="center">
						{isEditing && (
							<>
								<Button
									variant="solid"
									size="md"
									h="40px"
									w="128px"
									mb={4}
									bg="green"
									_hover={{ bg: 'darkgreen' }}
									color="white"
									rightIcon={<FontAwesomeIcon icon={faCalculator} style={{ fontSize: 16 }} />}
									onClick={onOpen}
									isLoading={loadingSaveLifeRatio}
									spinnerPlacement="start"
								>
									{t('Re-Calculate')}
								</Button>
								<Button
									variant="solid"
									size="md"
									h="40px"
									w="120px"
									ml={2}
									mb={4}
									bg="red"
									_hover={{ bg: 'darkred' }}
									color="white"
									rightIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
									onClick={() => {
										setQ2Cells(initialQ2Cells);
										setQ3Cells(initialQ3Cells);
										setIsEditing(false);
									}}
									isLoading={loadingSaveLifeRatio}
									spinnerPlacement="start"
								>
									{t('Cancel')}
								</Button>
							</>
						)}
					</Flex>
					<div className="ews-1-row-head-ratio">{t('Ratio Name')}</div>

					<Flex>
						{q1Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Norms')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q2Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Current Ratio')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q3Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Previous Ratio')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q4Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Current Risk Flag')}
										</td>
									</tr>
								</thead>
							</table>
						)}

						{q5Cells?.length > 0 && (
							<table className="spreadsheet_custom_header">
								<thead>
									<tr style={{ height: '34px' }}>
										<td colSpan="2" className="ews-background-light-silver">
											{t('Previous Risk Flag')}
										</td>
									</tr>
								</thead>
							</table>
						)}
					</Flex>

					<div>
						<EWSLabels type="EWS" labels={detail?.labels} active={active} />
						<Flex>
							{ratioCells?.map(({ cells, setCells, index }) =>
								cells?.length > 0 ? (
									<Spreadsheet
										key={index}
										data={cells}
										onChange={(newData) => {
											if (setCells) {
												handleCellsChange(index, newData);
											}

											if (index === 1) {
												const changed = isDifferent(newData, initialQ2Cells) || isDifferent(newData, initialQ3Cells);
												setIsEditing(changed);
											} else if (index === 2) {
												const changed = isDifferent(newData, initialQ3Cells) || isDifferent(newData, initialQ2Cells);
												setIsEditing(changed);
											}
										}}
										onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
										hideRowIndicators
										hideColumnIndicators
									/>
								) : null
							)}
						</Flex>
					</div>
				</Box>
			)}
			<AlertDialog isOpen={isOpen} leastDestructiveRef={undefined} onClose={onClose}>
				<AlertDialogOverlay />
				<AlertDialogContent>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						Re-Calculate
					</AlertDialogHeader>

					<AlertDialogBody>
						<Text fontSize="16px">Are you sure you want to re-calculate the ratios?</Text>
					</AlertDialogBody>

					<AlertDialogFooter>
						<Button onClick={onClose}>Cancel</Button>
						<Button
							colorScheme="green"
							onClick={() => {
								calculateButton();
								onClose();
								window.location.reload();
							}}
							ml={3}
						>
							{t('Confirm')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Box>
	);
}
